import {
  Box,
  Button,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  styled,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { HiOutlineMenu } from 'react-icons/hi';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { IconButton } from '../../styles';
import { FONT_STYLE } from '../../variables';
import CompanyLogo from '../CompanyLogo';

// Botão para setas de submenu (coleções/categorias)

const SubmenuArrowButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5)
}));

const MenuMobile = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const collections = useSelector((state) => state.categories.categories);

  const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(false);
  const [isCategoryMenuOpened, setIsCategoryMenuOpened] = useState(false);
  const [isSubcategoryMenuOpened, setIsSubcategoryMenuOpened] = useState(false);

  useEffect(() => {
    if (isMobileMenuOpened === false) {
      setIsCategoryMenuOpened(false);
    }
    if (isCategoryMenuOpened) {
      setIsSubcategoryMenuOpened(false);
    }
  }, [isCategoryMenuOpened, isMobileMenuOpened]);

  const SubcategoriesMenu = ({ subcategories, index }) => {
    return (
      <Collapse
        in={isSubcategoryMenuOpened[index]}
        timeout='auto'
        unmountOnExit
        sx={{
          width: '100%'
        }}
      >
        <List>
          {subcategories.map(({ title, url }) => (
            <ListItem
              key={`menu-subcategories-${title}`}
              sx={{
                paddingY: 0
              }}
            >
              <ListItemButton
                component={Link}
                to={url}
                title={t(title)}
                onClick={() => setIsMobileMenuOpened(false)}
                sx={{
                  textTransform: 'uppercase'
                }}
              >
                {t(title)}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    );
  };

  const CategoriesMenu = ({ categories, index }) => {
    return (
      <Collapse
        in={isCategoryMenuOpened[index]}
        timeout='auto'
        unmountOnExit
        sx={{
          width: '100%'
        }}
      >
        <List>
          {categories.map(
            ({ title, url, subcategories }, subcategoriesIndex) => (
              <ListItem
                key={`menu-categories-${title}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingY: 0,
                  paddingRight: 0
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%'
                  }}
                >
                  <ListItemButton
                    component={Link}
                    to={url}
                    title={t(title)}
                    onClick={() => setIsMobileMenuOpened(false)}
                    sx={{
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      fontWeight: isSubcategoryMenuOpened[subcategoriesIndex]
                        ? FONT_STYLE.bold
                        : null
                    }}
                  >
                    {t(title)}
                  </ListItemButton>
                  {!!subcategories.length && (
                    <SubmenuArrowButton
                      onClick={() =>
                        setIsSubcategoryMenuOpened({
                          [subcategoriesIndex]:
                            !isSubcategoryMenuOpened[subcategoriesIndex]
                        })
                      }
                    >
                      {isSubcategoryMenuOpened[subcategoriesIndex] ? (
                        <FiChevronUp size={16} />
                      ) : (
                        <FiChevronDown size={16} />
                      )}
                    </SubmenuArrowButton>
                  )}
                </Box>
                {!!subcategories.length && (
                  <SubcategoriesMenu
                    subcategories={subcategories}
                    index={subcategoriesIndex}
                  />
                )}
              </ListItem>
            )
          )}
        </List>
      </Collapse>
    );
  };

  return (
    <>
      <IconButton
        startIcon={<HiOutlineMenu size={28} />}
        onClick={() => setIsMobileMenuOpened(true)}
        title={t('app.menu')}
      />

      <Drawer
        open={isMobileMenuOpened}
        onClose={() => {
          setIsCategoryMenuOpened(false);
          setIsSubcategoryMenuOpened(false);
          setIsMobileMenuOpened(false);
        }}
        PaperProps={{
          style: {
            width: '100%',
            maxWidth: '85%',
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(3)
          }
        }}
        sx={{
          '& > .MuiListItem-root': {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <>
          <Box
            sx={{
              paddingX: 2
            }}
          >
            <CompanyLogo link={false} />
          </Box>
          <List
            sx={{
              paddingY: 3,
              fontSize: '1.1rem'
            }}
          >
            {!!collections?.length && (
              <>
                {collections
                  .filter((collection) => collection.highlighted)
                  .reduce((acc, highlightedCollection, index, array) => {
                    const collectionsWithoutHighlighted = collections.filter(
                      (collection) => !collection.highlighted
                    );
                    acc.push(highlightedCollection);
                    index === array.length - 1 &&
                      acc.push(...collectionsWithoutHighlighted);
                    return acc;
                  }, [])
                  .map(({ title, url, categories }, index) => (
                    <ListItem
                      disablePadding
                      key={`menu-mobile-${title}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        paddingY: 0
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          width: '100%'
                        }}
                      >
                        <ListItemButton
                          component={Link}
                          to={url}
                          title={t(title)}
                          onClick={() => setIsMobileMenuOpened(false)}
                          sx={{
                            fontSize: 'inherit',
                            textTransform: 'uppercase',
                            fontWeight:
                              isCategoryMenuOpened[index] && FONT_STYLE.bold
                          }}
                        >
                          {t(title)}
                        </ListItemButton>
                        {!!categories.length && (
                          <SubmenuArrowButton
                            onClick={() =>
                              setIsCategoryMenuOpened({
                                [index]: !isCategoryMenuOpened[index]
                              })
                            }
                          >
                            {isCategoryMenuOpened[index] ? (
                              <FiChevronUp size={16} />
                            ) : (
                              <FiChevronDown size={16} />
                            )}
                          </SubmenuArrowButton>
                        )}
                      </Box>
                      {!!categories.length && (
                        <CategoriesMenu categories={categories} index={index} />
                      )}
                    </ListItem>
                  ))}
              </>
            )}
          </List>
        </>
      </Drawer>
    </>
  );
};

export default MenuMobile;
